import {
	BlockchainNetwork,
	ChainIds,
	PolygonNetwork,
} from "@hfc/shared/typings/blockchain/Network"

export interface BlockchainConfig {
	network: BlockchainNetwork
	chainId: number
	rpcUrl: string
	wssUrl: string
	whitelistContractAddress: string
	traitsContractAddress: string
	hfcContractAddress: string
	hfcV2ContractAddress?: string
	migrationTractorAddress?: string
	metaFarmerAddress?: string
	revealFarmerAddress?: string
	whitelistFarmerContractAddress?: string
	theFarmAddress?: string
	theFarmV2Address?: string
	rewardFarmerAddress?: string
	farmGirlAddress?: string
	wheelOfFortuneAddress?: string
	potatoAddress?: string
}

export const config = {
	// Target Network
	NEXT_PUBLIC_BLOCKCHAIN: process.env
		.NEXT_PUBLIC_BLOCKCHAIN as BlockchainNetwork,

	// Rinkeby Testnet
	NEXT_PUBLIC_RINKEBY_RPC_URL: process.env.NEXT_PUBLIC_RINKEBY_RPC_URL,
	NEXT_PUBLIC_RINKEBY_WSS_URL: process.env.NEXT_PUBLIC_RINKEBY_WSS_URL,
	NEXT_PUBLIC_RINKEBY_WHITELIST_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_RINKEBY_WHITELIST_CONTRACT_ADDRESS,
	NEXT_PUBLIC_RINKEBY_TRAITS_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_RINKEBY_TRAITS_CONTRACT_ADDRESS,
	NEXT_PUBLIC_RINKEBY_HFC_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_RINKEBY_HFC_CONTRACT_ADDRESS,

	// Mumbai Testnet
	NEXT_PUBLIC_MUMBAI_RPC_URL: process.env.NEXT_PUBLIC_MUMBAI_RPC_URL,
	NEXT_PUBLIC_MUMBAI_WSS_URL: process.env.NEXT_PUBLIC_MUMBAI_WSS_URL,
	NEXT_PUBLIC_MUMBAI_WHITELIST_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_MUMBAI_WHITELIST_CONTRACT_ADDRESS,
	NEXT_PUBLIC_MUMBAI_TRAITS_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_MUMBAI_TRAITS_CONTRACT_ADDRESS,
	NEXT_PUBLIC_MUMBAI_HFC_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_MUMBAI_HFC_CONTRACT_ADDRESS,

	// Polygon Mainnet
	NEXT_PUBLIC_POLYGON_RPC_URL: process.env.NEXT_PUBLIC_POLYGON_RPC_URL,
	NEXT_PUBLIC_POLYGON_WSS_URL: process.env.NEXT_PUBLIC_POLYGON_WSS_URL,
	NEXT_PUBLIC_POLYGON_WHITELIST_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_WHITELIST_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_TRAITS_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_TRAITS_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_HFC_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_HFC_CONTRACT_ADDRESS,

	// V2
	NEXT_PUBLIC_POLYGON_V1_HFC_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V1_HFC_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_HFC_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_HFC_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_ENERGY_FARMER_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_ENERGY_FARMER_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_META_FARMER_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_META_FARMER_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_MIGRATION_TRACTOR_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_MIGRATION_TRACTOR_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_WHITELIST_FARMER_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_WHITELIST_FARMER_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_REVEAL_FARMER_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_REVEAL_FARMER_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_REGISTRY_FARMER_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_REGISTRY_FARMER_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_THE_FARM_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_THE_FARM_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_THE_FARM_V2_CONTRACT_ADDRESS:
		"0x6e0bb09b0ae7b28e171dfb01c8b42d28d2a10c2e",
	NEXT_PUBLIC_POLYGON_V2_POTATO_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_POTATO_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_REWARD_FARMER_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_REWARD_FARMER_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_FARM_GIRLS_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_FARM_GIRLS_CONTRACT_ADDRESS,
	NEXT_PUBLIC_POLYGON_V2_WHEEL_OF_FORTUNE_CONTRACT_ADDRESS:
		process.env.NEXT_PUBLIC_POLYGON_V2_WHEEL_OF_FORTUNE_CONTRACT_ADDRESS,
}

export const fallbackChain: PolygonNetwork = "polygon"
export const fallbackChainId: number = ChainIds?.[fallbackChain] ?? 137

export const targetChain = config.NEXT_PUBLIC_BLOCKCHAIN
export const targetChainId = ChainIds?.[targetChain] ?? fallbackChainId

const getBlockchainConfig = (): BlockchainConfig => {
	switch (config.NEXT_PUBLIC_BLOCKCHAIN) {
		case "rinkeby":
			return {
				network: "rinkeby",
				chainId: ChainIds.rinkeby,
				rpcUrl: config.NEXT_PUBLIC_RINKEBY_RPC_URL,
				wssUrl: config.NEXT_PUBLIC_RINKEBY_WSS_URL,
				whitelistContractAddress:
					config.NEXT_PUBLIC_RINKEBY_WHITELIST_CONTRACT_ADDRESS,
				traitsContractAddress:
					config.NEXT_PUBLIC_RINKEBY_TRAITS_CONTRACT_ADDRESS,
				hfcContractAddress:
					config.NEXT_PUBLIC_RINKEBY_HFC_CONTRACT_ADDRESS,
			} as BlockchainConfig

		case "mumbai":
			return {
				network: "mumbai",
				chainId: ChainIds.mumbai,
				rpcUrl: config.NEXT_PUBLIC_MUMBAI_RPC_URL,
				wssUrl: config.NEXT_PUBLIC_MUMBAI_WSS_URL,
				whitelistContractAddress:
					config.NEXT_PUBLIC_MUMBAI_WHITELIST_CONTRACT_ADDRESS,
				traitsContractAddress:
					config.NEXT_PUBLIC_MUMBAI_TRAITS_CONTRACT_ADDRESS,
				hfcContractAddress:
					config.NEXT_PUBLIC_MUMBAI_HFC_CONTRACT_ADDRESS,
			} as BlockchainConfig

		case "polygon":
		default:
			return {
				network: "polygon",
				chainId: ChainIds.polygon,
				rpcUrl: config.NEXT_PUBLIC_POLYGON_RPC_URL,
				wssUrl: config.NEXT_PUBLIC_POLYGON_WSS_URL,
				whitelistContractAddress:
					config.NEXT_PUBLIC_POLYGON_WHITELIST_CONTRACT_ADDRESS,
				traitsContractAddress:
					config.NEXT_PUBLIC_POLYGON_TRAITS_CONTRACT_ADDRESS,
				hfcContractAddress:
					config.NEXT_PUBLIC_POLYGON_HFC_CONTRACT_ADDRESS,
				hfcV2ContractAddress:
					config.NEXT_PUBLIC_POLYGON_V2_HFC_CONTRACT_ADDRESS,
				migrationTractorAddress:
					config.NEXT_PUBLIC_POLYGON_V2_MIGRATION_TRACTOR_CONTRACT_ADDRESS,
				metaFarmerAddress:
					config.NEXT_PUBLIC_POLYGON_V2_META_FARMER_CONTRACT_ADDRESS,
				revealFarmerAddress:
					config.NEXT_PUBLIC_POLYGON_V2_REVEAL_FARMER_CONTRACT_ADDRESS,
				whitelistFarmerContractAddress:
					config.NEXT_PUBLIC_POLYGON_V2_WHITELIST_FARMER_CONTRACT_ADDRESS,
				theFarmAddress:
					config.NEXT_PUBLIC_POLYGON_V2_THE_FARM_CONTRACT_ADDRESS,
				theFarmV2Address:
					config.NEXT_PUBLIC_POLYGON_V2_THE_FARM_V2_CONTRACT_ADDRESS,
				potatoAddress:
					config.NEXT_PUBLIC_POLYGON_V2_POTATO_CONTRACT_ADDRESS,
				rewardFarmerAddress:
					config.NEXT_PUBLIC_POLYGON_V2_REWARD_FARMER_CONTRACT_ADDRESS,
				farmGirlAddress:
					config.NEXT_PUBLIC_POLYGON_V2_FARM_GIRLS_CONTRACT_ADDRESS,
				wheelOfFortuneAddress:
					config.NEXT_PUBLIC_POLYGON_V2_WHEEL_OF_FORTUNE_CONTRACT_ADDRESS,
			} as BlockchainConfig
	}
}

export default getBlockchainConfig
